import * as amplitude from "@amplitude/analytics-browser";
import { lazyComponent } from "@redotech/react-util/component";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import EditSquareIcon from "@redotech/redo-icons/edit-05.svg";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { RedoIcon } from "../../../arbiter-components/icon/redo-icon";
import { Dropdown } from "../../../dropdown";
import { Flex } from "../../../flex";
import { LoadingRedoAnimation } from "../../../loading-redo-animation";
import { Text } from "../../../text";
import { Tooltip } from "../../../tooltip/tooltip";
import { useAiSlidePanel } from "../contexts/ai-slide-panel-context";

const ChatWithThread = lazyComponent(async () => {
  const { ChatWithThread } = await import("./chat-with-thread");
  return ChatWithThread;
});

interface ChatThreadMessage {
  id: string;
  role: string;
  content: string | unknown[];
}

interface ChatThread {
  _id: string;
  updatedAt: string | Date;
  messages: ChatThreadMessage[];
}

function getThreadTitle(thread: ChatThread): string {
  const firstUserContent = thread.messages.find(
    (msg) => msg.role === "user",
  )?.content;
  if (typeof firstUserContent === "string") {
    return firstUserContent;
  }
  if (Array.isArray(firstUserContent)) {
    const textPart = firstUserContent.find(
      (p): p is { type: string; text: string } =>
        typeof p === "object" &&
        p !== null &&
        "type" in p &&
        "text" in p &&
        (p as { type: unknown }).type === "text" &&
        typeof (p as { text: unknown }).text === "string",
    );
    if (textPart) {
      return textPart.text;
    }
  }
  return `Conversation ${thread._id.slice(-6)}`;
}

export const AiSlideOutChatGlobal = memo(function AiSlideOutChatGlobal({
  onClose,
  belowChatButtons,
  suggestedQuestions,
  threads = [],
  currentThreadId,
  onLoadThread,
  onNewConversation,
  hideClearButton,
  disableChatInput,
  leadingToolbarContent,
  aboveInput,
  isLoadingThread,
}: {
  onClose: () => void;
  belowChatButtons?: { text: string; onClick: () => void }[];
  suggestedQuestions?: string[];
  threads?: ChatThread[];
  currentThreadId?: string;
  onLoadThread?: (threadId: string, thread: ChatThread) => void | Promise<void>;
  onNewConversation?: () => void;
  hideClearButton?: boolean;
  disableChatInput?: boolean;
  leadingToolbarContent?: ReactNode;
  aboveInput?: ReactNode;
  isLoadingThread?: boolean;
}) {
  const { panelWidth } = useAiSlidePanel();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const chevronRef = useRef<HTMLDivElement>(null);

  // Check if there are any threads to show
  const hasThreads = threads && threads.length > 0;

  const handleChevronClick = useCallback(() => {
    // Only handle click if there are threads to show
    if (!hasThreads) {
      return;
    }

    if (!dropdownOpen) {
      amplitude.logEvent("merchant-support/thread-history-dropdown-opened");
    }
    setDropdownOpen(!dropdownOpen);
  }, [dropdownOpen, hasThreads]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownOpen &&
        chevronRef.current &&
        !chevronRef.current.contains(event.target as Node)
      ) {
        // Check if click is not inside the dropdown content
        const dropdownElement = document.getElementById("thread-dropdown");
        if (
          !dropdownElement ||
          !dropdownElement.contains(event.target as Node)
        ) {
          setDropdownOpen(false);
        }
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <div
      data-exclude-text-highlight="true"
      style={{
        height: "100%",
        overflow: "hidden",
        background: "var(--color-fg-secondary)",
        borderRadius: "var(--redo-corner-xsmall-radius)",
      }}
    >
      <Flex
        bgColor="secondary"
        borderColor="primary"
        dir="column"
        height="full"
        overflow="hidden"
        pr="md"
        style={{ minWidth: `${panelWidth}px` }}
      >
        <Flex
          align="center"
          flexDirection="row"
          justify="space-between"
          px="lg"
          py="md"
        >
          <Flex
            align="center"
            onClick={handleChevronClick}
            onMouseEnter={(e) => {
              if (hasThreads) {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.05)";
              }
            }}
            onMouseLeave={(e) => {
              if (hasThreads) {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
            ref={chevronRef}
            style={{
              cursor: hasThreads ? "pointer" : "default",
              padding: "4px 8px",
              borderRadius: "6px",
              backgroundColor: "transparent",
              transition: "background-color 0.15s ease",
            }}
          >
            <Text fontSize="sm" fontWeight="medium">
              Chat history
            </Text>
            {hasThreads && (
              <RedoIcon color="secondary" Icon={ChevronDownIcon} size={16} />
            )}
          </Flex>
          <Flex align="center" gap="xs">
            <Tooltip arrow placement="bottom" title="New conversation">
              <RedoButton
                aria-label="New conversation"
                data-testid="new-conversation-button"
                hierarchy="tertiary"
                IconLeading={EditSquareIcon}
                onClick={onNewConversation}
                size="sm"
              />
            </Tooltip>
            <RedoButton
              aria-label="Close"
              data-testid="chat-close-button"
              hierarchy="tertiary"
              IconLeading={CloseIcon}
              onClick={onClose}
              size="sm"
            />
          </Flex>
        </Flex>

        <Flex
          dir="column"
          style={{ position: "relative", flex: 1, minHeight: 0 }}
        >
          <ChatWithThread
            aboveInput={aboveInput}
            belowChatButtons={belowChatButtons}
            disableChatInput={disableChatInput}
            hideClearButton={hideClearButton}
            leadingToolbarContent={leadingToolbarContent}
            size="sm"
            style="high-contrast"
            suggestedQuestions={suggestedQuestions}
          />
          {isLoadingThread && (
            <Flex
              align="center"
              justify="center"
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "var(--redo-colors-background-bg-secondary)",
                zIndex: 10,
              }}
            >
              <LoadingRedoAnimation />
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* Thread History Dropdown - Only render if there are threads */}
      {hasThreads && (
        <Dropdown
          anchor={chevronRef.current}
          fitToAnchor={false}
          flexProps={{
            id: "thread-dropdown",
            style: {
              width: "280px",
              maxHeight: "400px",
              overflowY: "auto",
              marginLeft: "-120px",
            },
          }}
          open={dropdownOpen}
          placement="bottom-start"
        >
          {(() => {
            // Group threads by time periods
            const now = new Date();
            const today = new Date(
              now.getFullYear(),
              now.getMonth(),
              now.getDate(),
            );
            const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            const monthAgo = new Date(
              today.getTime() - 30 * 24 * 60 * 60 * 1000,
            );

            const groups = {
              today: [] as ChatThread[],
              yesterday: [] as ChatThread[],
              thisWeek: [] as ChatThread[],
              thisMonth: [] as ChatThread[],
            };

            for (const thread of threads) {
              const threadDate = new Date(thread.updatedAt);
              if (threadDate >= today) {
                groups.today.push(thread);
              } else if (threadDate >= yesterday) {
                groups.yesterday.push(thread);
              } else if (threadDate >= weekAgo) {
                groups.thisWeek.push(thread);
              } else if (threadDate >= monthAgo) {
                groups.thisMonth.push(thread);
              }
            }

            return (
              <>
                {groups.today.length > 0 && (
                  <>
                    <Text
                      color="primary"
                      fontSize="sm"
                      fontWeight="semibold"
                      style={{ marginBottom: "4px", marginTop: "0px" }}
                    >
                      Today
                    </Text>
                    {groups.today.map((thread) => {
                      const isCurrentThread = currentThreadId === thread._id;
                      const threadTitle = getThreadTitle(thread);

                      return (
                        <div
                          key={thread._id}
                          onClick={() => {
                            void onLoadThread?.(thread._id, thread);
                            setDropdownOpen(false);
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "var(--color-bg-secondary-subtle)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                          style={{
                            padding: "2px 4px",
                            marginBottom: "1px",
                            borderRadius: "2px",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            backgroundColor: isCurrentThread
                              ? "var(--color-bg-brand-subtle)"
                              : "transparent",
                            border: isCurrentThread
                              ? "1px solid var(--color-border-brand)"
                              : "1px solid transparent",
                          }}
                        >
                          <Flex align="center" gap="xs">
                            <Text
                              fontSize="sm"
                              fontWeight="regular"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "180px",
                              }}
                            >
                              {threadTitle}
                            </Text>
                            {isCurrentThread && (
                              <Text
                                color="brand"
                                fontSize="xs"
                                fontWeight="medium"
                              >
                                (Current)
                              </Text>
                            )}
                          </Flex>
                        </div>
                      );
                    })}
                  </>
                )}

                {groups.yesterday.length > 0 && (
                  <>
                    <Text
                      color="primary"
                      fontSize="sm"
                      fontWeight="semibold"
                      style={{
                        marginBottom: "4px",
                        marginTop: groups.today.length > 0 ? "8px" : "0px",
                      }}
                    >
                      Yesterday
                    </Text>
                    {groups.yesterday.map((thread) => {
                      const isCurrentThread = currentThreadId === thread._id;
                      const threadTitle = getThreadTitle(thread);

                      return (
                        <div
                          key={thread._id}
                          onClick={() => {
                            void onLoadThread?.(thread._id, thread);
                            setDropdownOpen(false);
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "var(--color-bg-secondary-subtle)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                          style={{
                            padding: "2px 4px",
                            marginBottom: "1px",
                            borderRadius: "2px",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            backgroundColor: isCurrentThread
                              ? "var(--color-bg-brand-subtle)"
                              : "transparent",
                            border: isCurrentThread
                              ? "1px solid var(--color-border-brand)"
                              : "1px solid transparent",
                          }}
                        >
                          <Flex align="center" gap="xs">
                            <Text
                              fontSize="sm"
                              fontWeight="regular"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "180px",
                              }}
                            >
                              {threadTitle}
                            </Text>
                            {isCurrentThread && (
                              <Text
                                color="brand"
                                fontSize="xs"
                                fontWeight="medium"
                              >
                                (Current)
                              </Text>
                            )}
                          </Flex>
                        </div>
                      );
                    })}
                  </>
                )}

                {groups.thisWeek.length > 0 && (
                  <>
                    <Text
                      color="primary"
                      fontSize="sm"
                      fontWeight="semibold"
                      style={{
                        marginBottom: "4px",
                        marginTop:
                          groups.today.length > 0 || groups.yesterday.length > 0
                            ? "8px"
                            : "0px",
                      }}
                    >
                      This week
                    </Text>
                    {groups.thisWeek.map((thread) => {
                      const isCurrentThread = currentThreadId === thread._id;
                      const threadTitle = getThreadTitle(thread);

                      return (
                        <div
                          key={thread._id}
                          onClick={() => {
                            void onLoadThread?.(thread._id, thread);
                            setDropdownOpen(false);
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "var(--color-bg-secondary-subtle)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                          style={{
                            padding: "2px 4px",
                            marginBottom: "1px",
                            borderRadius: "2px",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            backgroundColor: isCurrentThread
                              ? "var(--color-bg-brand-subtle)"
                              : "transparent",
                            border: isCurrentThread
                              ? "1px solid var(--color-border-brand)"
                              : "1px solid transparent",
                          }}
                        >
                          <Flex align="center" gap="xs">
                            <Text
                              fontSize="sm"
                              fontWeight="regular"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "180px",
                              }}
                            >
                              {threadTitle}
                            </Text>
                            {isCurrentThread && (
                              <Text
                                color="brand"
                                fontSize="xs"
                                fontWeight="medium"
                              >
                                (Current)
                              </Text>
                            )}
                          </Flex>
                        </div>
                      );
                    })}
                  </>
                )}

                {groups.thisMonth.length > 0 && (
                  <>
                    <Text
                      color="primary"
                      fontSize="sm"
                      fontWeight="semibold"
                      style={{
                        marginBottom: "4px",
                        marginTop:
                          groups.today.length > 0 ||
                          groups.yesterday.length > 0 ||
                          groups.thisWeek.length > 0
                            ? "8px"
                            : "0px",
                      }}
                    >
                      This month
                    </Text>
                    {groups.thisMonth.map((thread) => {
                      const isCurrentThread = currentThreadId === thread._id;
                      const threadTitle = getThreadTitle(thread);

                      return (
                        <div
                          key={thread._id}
                          onClick={() => {
                            void onLoadThread?.(thread._id, thread);
                            setDropdownOpen(false);
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "var(--color-bg-secondary-subtle)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                          style={{
                            padding: "2px 4px",
                            marginBottom: "1px",
                            borderRadius: "2px",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            backgroundColor: isCurrentThread
                              ? "var(--color-bg-brand-subtle)"
                              : "transparent",
                            border: isCurrentThread
                              ? "1px solid var(--color-border-brand)"
                              : "1px solid transparent",
                          }}
                        >
                          <Flex align="center" gap="xs">
                            <Text
                              fontSize="sm"
                              fontWeight="regular"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "180px",
                              }}
                            >
                              {threadTitle}
                            </Text>
                            {isCurrentThread && (
                              <Text
                                color="brand"
                                fontSize="xs"
                                fontWeight="medium"
                              >
                                (Current)
                              </Text>
                            )}
                          </Flex>
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            );
          })()}
        </Dropdown>
      )}
    </div>
  );
});

export { AiSlideOutChatGlobal as AiSlideOutChatAdvanced };
